import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronDown, 
  ChevronUp, 
  Search, 
  HelpCircle, 
  Phone, 
  Mail, 
  MessageCircle,
  Heart,
  Calendar,
  CreditCard,
  Shield,
  Users,
  Clock,
  FileText,
  AlertCircle,
  Smartphone,
  Globe,
  Lock,
  CheckCircle,
  XCircle
} from 'lucide-react';

/**
 * FAQPage Component
 * 
 * Based on GitHub best practices and patterns from:
 * - Healthcare React applications (OpenMRS, Medplum)
 * - Ant Design and Material-UI responsive patterns
 * - Accessible accordion patterns from W3C WAI-ARIA
 * 
 * Features:
 * - Tablet-optimized responsive layout (768px - 1023px)
 * - Searchable FAQ items with dynamic filtering
 * - Categorized questions for better navigation
 * - Smooth accordion animations
 * - Accessibility features (ARIA labels, keyboard navigation)
 * - Contact section for additional help
 * - Integration with Rhythm of life app routing structure from App.js
 * 
 * @component
 * @returns {React.Component} FAQ page with searchable accordion
 */

const FAQPage = () => {
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedItems, setExpandedItems] = useState({});
  const [filteredFAQs, setFilteredFAQs] = useState([]);
  const searchInputRef = useRef(null);

  // FAQ Categories with icons and colors (matching Rhythm of life design system)
  const categories = [
    { id: 'all', name: 'All Questions', icon: <HelpCircle className="w-5 h-5" />, color: 'bg-gray-100' },
    { id: 'general', name: 'General', icon: <Globe className="w-5 h-5" />, color: 'bg-blue-100' },
    { id: 'appointments', name: 'Appointments', icon: <Calendar className="w-5 h-5" />, color: 'bg-green-100' },
    { id: 'payment', name: 'Payment & Insurance', icon: <CreditCard className="w-5 h-5" />, color: 'bg-yellow-100' },
    { id: 'privacy', name: 'Privacy & Security', icon: <Shield className="w-5 h-5" />, color: 'bg-purple-100' },
    { id: 'technical', name: 'Technical Support', icon: <Smartphone className="w-5 h-5" />, color: 'bg-indigo-100' },
    { id: 'clinical', name: 'Clinical Services', icon: <Heart className="w-5 h-5" />, color: 'bg-red-100' }
  ];

  // Comprehensive FAQ data structure
  const faqData = [
    // General Questions
    {
      id: 1,
      category: 'general',
      question: 'What is Rhythm of life and how does it work?',
      answer: 'Rhythm of life is a comprehensive mental healthcare platform that connects patients with qualified mental health professionals. Our platform offers online consultations, appointment scheduling, therapy sessions, mood tracking tools, and secure medical record management. You can access our services through web, mobile, or desktop applications.',
      priority: 1
    },
    {
      id: 2,
      category: 'general',
      question: 'Who are the mental health professionals on Rhythm of life?',
      answer: 'Our platform features licensed psychiatrists, clinical psychologists, counselors, and social workers. All professionals are verified and have completed rigorous background checks. You can view their qualifications, specializations, and patient reviews on their profile pages before booking an appointment.',
      priority: 2
    },
    {
      id: 3,
      category: 'general',
      question: 'Is Rhythm of life available in my area?',
      answer: 'Rhythm of life provides online consultations accessible from anywhere in India. For in-person appointments, we have partner clinics in major cities including Bangalore, Mumbai, Delhi, Chennai, and Kolkata. Check our "Find Provider" section to locate services near you.',
      priority: 3
    },
    
    // Appointment Questions
    {
      id: 4,
      category: 'appointments',
      question: 'How do I book an appointment?',
      answer: 'You can book appointments through multiple ways: 1) Use the "Book Appointment" button on the homepage, 2) Browse providers in "Find Provider" section and select your preferred professional, 3) Use Quick Booking for urgent consultations, or 4) Call our helpline at +1 (888) 404-7470 for assistance.',
      priority: 1
    },
    {
      id: 5,
      category: 'appointments',
      question: 'Can I reschedule or cancel my appointment?',
      answer: 'Yes, you can reschedule or cancel appointments up to 24 hours before the scheduled time without any charges. Navigate to "My Appointments" in your patient portal, select the appointment, and choose reschedule or cancel. For appointments within 24 hours, please contact our support team.',
      priority: 2
    },
    {
      id: 6,
      category: 'appointments',
      question: 'What types of consultations are available?',
      answer: 'We offer: 1) Video consultations for remote sessions, 2) In-person appointments at partner clinics, 3) Group therapy sessions, 4) Emergency consultations for crisis situations, and 5) Follow-up appointments for ongoing treatment. Each type can be selected during the booking process.',
      priority: 3
    },
    {
      id: 7,
      category: 'appointments',
      question: 'How long are typical consultation sessions?',
      answer: 'Initial psychiatric consultations typically last 45-60 minutes, follow-up sessions are 20-30 minutes. Psychotherapy sessions are usually 50 minutes. Group therapy sessions run for 60-90 minutes. Emergency consultations are 15-30 minutes depending on urgency.',
      priority: 4
    },
    
    // Payment & Insurance Questions
    {
      id: 8,
      category: 'payment',
      question: 'What payment methods are accepted?',
      answer: 'We accept multiple payment options: Credit/Debit cards (Visa, Mastercard, Rupay), UPI payments (Google Pay, PhonePe, Paytm), Net Banking, Wallets, and Cash payments at partner clinics. All online transactions are secured with 256-bit encryption.',
      priority: 1
    },
    {
      id: 9,
      category: 'payment',
      question: 'Does insurance cover mental health consultations?',
      answer: 'Many insurance providers now cover mental health services. We work with major insurance companies including HDFC Ergo, ICICI Lombard, Star Health, and Max Bupa. Check with your insurance provider or contact our billing team to verify your coverage. We can provide necessary documentation for reimbursement claims.',
      priority: 2
    },
    {
      id: 10,
      category: 'payment',
      question: 'What are the consultation fees?',
      answer: 'Fees vary by provider and service type. Typically: Psychiatrist consultations: ₹1,500-3,000, Psychologist sessions: ₹1,000-2,000, Counselor sessions: ₹800-1,500, Group therapy: ₹500-800 per session. You can view exact fees on each provider\'s profile before booking.',
      priority: 3
    },
    {
      id: 11,
      category: 'payment',
      question: 'How do I get a refund?',
      answer: 'Refunds are processed for cancellations made 24 hours before appointment. The amount is credited to your original payment method within 5-7 business days. For technical issues during consultations, contact support within 24 hours for resolution or refund consideration.',
      priority: 4
    },
    
    // Privacy & Security Questions
    {
      id: 12,
      category: 'privacy',
      question: 'How is my medical information protected?',
      answer: 'We use industry-standard security measures including: 256-bit SSL encryption for all data transmission, HIPAA-compliant storage systems, Multi-factor authentication, Regular security audits, and strict access controls. Your data is never shared without explicit consent except as required by law.',
      priority: 1
    },
    {
      id: 13,
      category: 'privacy',
      question: 'Who can access my medical records?',
      answer: 'Only you and your authorized healthcare providers can access your medical records. You can grant or revoke access to specific providers through your patient portal. Our staff can only access technical information needed for support, never your clinical data.',
      priority: 2
    },
    {
      id: 14,
      category: 'privacy',
      question: 'Are video consultations private and secure?',
      answer: 'Yes, all video consultations use end-to-end encryption. Sessions are not recorded without explicit consent. We use secure, HIPAA-compliant video platforms. Only you and your provider are present in the session unless you authorize additional participants.',
      priority: 3
    },
    
    // Technical Support Questions
    {
      id: 15,
      category: 'technical',
      question: 'What are the system requirements for video consultations?',
      answer: 'For optimal experience: Internet speed of at least 2 Mbps, Updated web browser (Chrome, Firefox, Safari, Edge), Webcam and microphone (built-in or external), For mobile: iOS 12+ or Android 8+. The platform works on tablets, laptops, and desktops.',
      priority: 1
    },
    {
      id: 16,
      category: 'technical',
      question: 'I\'m having trouble logging in. What should I do?',
      answer: 'Try these steps: 1) Click "Forgot Password" to reset your password, 2) Clear browser cache and cookies, 3) Try a different browser, 4) Ensure you\'re using the correct email/phone number, 5) Check if caps lock is on. If issues persist, contact support at support@rhythmoflife.com.',
      priority: 2
    },
    {
      id: 17,
      category: 'technical',
      question: 'Is there a mobile app available?',
      answer: 'Yes, Rhythm of life mobile apps are available for both iOS and Android devices. Download from the App Store or Google Play Store. The app includes all features available on the web version, optimized for mobile use with offline mood tracking capabilities.',
      priority: 3
    },
    
    // Clinical Services Questions
    {
      id: 18,
      category: 'clinical',
      question: 'What mental health conditions do you treat?',
      answer: 'We provide treatment for a wide range of conditions including: Depression, Anxiety disorders, Bipolar disorder, PTSD, OCD, Eating disorders, Substance use disorders, ADHD, Sleep disorders, Relationship issues, and Stress management. Our specialists can provide appropriate diagnosis and treatment plans.',
      priority: 1
    },
    {
      id: 19,
      category: 'clinical',
      question: 'Do you provide emergency mental health services?',
      answer: 'Yes, we have a 24/7 crisis helpline at +1 (888) 404-7470. For immediate danger, please call 112 or visit the nearest emergency room. Our platform also offers urgent consultation slots for crisis situations that aren\'t immediately life-threatening.',
      priority: 2
    },
    {
      id: 20,
      category: 'clinical',
      question: 'Can I get prescriptions through Rhythm of life?',
      answer: 'Licensed psychiatrists on our platform can prescribe medications after proper evaluation. E-prescriptions are sent directly to your registered email and available in your patient portal. You can fill prescriptions at any pharmacy. We also offer medication reminder features.',
      priority: 3
    },
    {
      id: 21,
      category: 'clinical',
      question: 'What assessment tools do you use?',
      answer: 'We use standardized, evidence-based assessment tools including: PHQ-9 for depression, GAD-7 for anxiety, MDQ for bipolar screening, PCL-5 for PTSD, Y-BOCS for OCD, and AUDIT for substance use. These help track your progress and guide treatment decisions.',
      priority: 4
    }
  ];

  // Filter FAQs based on search and category
  useEffect(() => {
    let filtered = faqData;
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === selectedCategory);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(faq => 
        faq.question.toLowerCase().includes(query) || 
        faq.answer.toLowerCase().includes(query)
      );
    }
    
    // Sort by priority
    filtered.sort((a, b) => a.priority - b.priority);
    
    setFilteredFAQs(filtered);
  }, [searchQuery, selectedCategory]);

  // Toggle FAQ item expansion
  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Expand all items in category
  const expandAll = () => {
    const newExpanded = {};
    filteredFAQs.forEach(faq => {
      newExpanded[faq.id] = true;
    });
    setExpandedItems(newExpanded);
  };

  // Collapse all items
  const collapseAll = () => {
    setExpandedItems({});
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, id) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpand(id);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-sky-500 to-cyan-600 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-3">
                Frequently Asked Questions
              </h1>
              <p className="text-lg md:text-xl opacity-95">
                Find answers to common questions about our mental healthcare services
              </p>
            </div>
            <HelpCircle className="w-16 h-16 md:w-20 md:h-20 mt-4 md:mt-0 opacity-80" />
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="sticky top-0 z-40 bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 text-gray-700"
                aria-label="Search FAQs"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Category Filters - Tablet Optimized Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`
                  flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all
                  ${selectedCategory === category.id 
                    ? 'bg-sky-500 text-white shadow-md transform scale-105' 
                    : `${category.color} hover:shadow-md`
                  }
                `}
                aria-pressed={selectedCategory === category.id}
              >
                {category.icon}
                <span className="text-sm font-medium hidden sm:inline">
                  {category.name}
                </span>
                <span className="text-sm font-medium sm:hidden">
                  {category.name.split(' ')[0]}
                </span>
              </button>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-gray-600">
              Found {filteredFAQs.length} {filteredFAQs.length === 1 ? 'question' : 'questions'}
            </div>
            <div className="flex gap-2">
              <button
                onClick={expandAll}
                className="text-sm text-sky-600 hover:text-sky-700 font-medium"
                aria-label="Expand all questions"
              >
                Expand All
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={collapseAll}
                className="text-sm text-sky-600 hover:text-sky-700 font-medium"
                aria-label="Collapse all questions"
              >
                Collapse All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Content - Main Section */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* FAQ Accordion - Takes 2/3 on desktop, full on tablet */}
          <div className="lg:col-span-2">
            {filteredFAQs.length > 0 ? (
              <div className="space-y-4">
                {filteredFAQs.map((faq) => (
                  <div
                    key={faq.id}
                    className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow"
                  >
                    <button
                      onClick={() => toggleExpand(faq.id)}
                      onKeyDown={(e) => handleKeyDown(e, faq.id)}
                      className="w-full px-6 py-4 flex justify-between items-start text-left focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-inset rounded-lg"
                      aria-expanded={expandedItems[faq.id] || false}
                      aria-controls={`faq-answer-${faq.id}`}
                    >
                      <div className="flex-1 pr-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {faq.question}
                        </h3>
                        {/* Category badge */}
                        <span className="inline-block mt-2 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                          {categories.find(c => c.id === faq.category)?.name}
                        </span>
                      </div>
                      <div className="flex-shrink-0 ml-2">
                        {expandedItems[faq.id] ? (
                          <ChevronUp className="w-5 h-5 text-sky-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </button>
                    
                    {/* Animated Answer Section */}
                    <div
                      id={`faq-answer-${faq.id}`}
                      className={`
                        overflow-hidden transition-all duration-300 ease-in-out
                        ${expandedItems[faq.id] ? 'max-h-96' : 'max-h-0'}
                      `}
                      aria-hidden={!expandedItems[faq.id]}
                    >
                      <div className="px-6 pb-4">
                        <div className="border-t pt-4">
                          <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No questions found
                </h3>
                <p className="text-gray-600">
                  Try adjusting your search or filter criteria
                </p>
              </div>
            )}
          </div>

          {/* Sidebar - Contact & Help Section */}
          <div className="lg:col-span-1">
            {/* Still Need Help Card */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6 sticky top-32">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Still Need Help?
              </h2>
              <p className="text-gray-600 mb-6">
                Can't find the answer you're looking for? Our support team is here to help.
              </p>
              
              {/* Contact Options */}
              <div className="space-y-4">
                {/* Phone Support */}
                <a
                  href="tel:18005990019"
                  className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors group"
                >
                  <div className="bg-green-100 p-2 rounded-lg group-hover:bg-green-200">
                    <Phone className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">Call Us</div>
                    <div className="text-sm text-gray-600">+1 (888) 404-7470</div>
                    <div className="text-xs text-gray-500">24/7 Crisis Helpline</div>
                  </div>
                </a>

                {/* Email Support */}
                <a
                  href="mailto:support@rhythmoflife.com"
                  className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors group"
                >
                  <div className="bg-blue-100 p-2 rounded-lg group-hover:bg-blue-200">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">Email Support</div>
                    <div className="text-sm text-gray-600">support@rhythmoflife.com</div>
                    <div className="text-xs text-gray-500">Response within 24 hours</div>
                  </div>
                </a>

                {/* Live Chat */}
                <button
                  onClick={() => {
                    // Implement live chat functionality
                    console.log('Opening live chat...');
                  }}
                  className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors group w-full text-left"
                >
                  <div className="bg-purple-100 p-2 rounded-lg group-hover:bg-purple-200">
                    <MessageCircle className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">Live Chat</div>
                    <div className="text-sm text-gray-600">Chat with our team</div>
                    <div className="text-xs text-gray-500">Available 9 AM - 9 PM</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Quick Links Card */}
            <div className="bg-gradient-to-br from-sky-50 to-cyan-50 rounded-lg p-6 border border-sky-200">
              <h3 className="font-bold text-gray-900 mb-4">Quick Links</h3>
              <div className="space-y-2">
                <a href="/mental-health-guide" className="block text-sky-600 hover:text-sky-700 hover:underline">
                  Mental Health Guide →
                </a>
                <a href="/self-help" className="block text-sky-600 hover:text-sky-700 hover:underline">
                  Self-Help Tools →
                </a>
                <a href="/booking/find-provider" className="block text-sky-600 hover:text-sky-700 hover:underline">
                  Find a Provider →
                </a>
                <a href="/emergency" className="block text-sky-600 hover:text-sky-700 hover:underline">
                  Emergency Support →
                </a>
              </div>
            </div>

            {/* Operating Hours */}
            <div className="bg-gray-50 rounded-lg p-6 mt-6 border">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-gray-600" />
                <h3 className="font-bold text-gray-900">Support Hours</h3>
              </div>
              <div className="text-sm space-y-1 text-gray-600">
                <div className="flex justify-between">
                  <span>Monday - Friday</span>
                  <span className="font-medium">9:00 AM - 9:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday</span>
                  <span className="font-medium">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span className="font-medium">10:00 AM - 4:00 PM</span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t text-xs text-green-600 font-medium">
                24/7 Crisis Helpline Available
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Section */}
      <div className="bg-gray-100 border-t mt-12">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Was this helpful?
            </h2>
            <p className="text-gray-600 mb-6">
              Your feedback helps us improve our support resources
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => console.log('Helpful feedback')}
                className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Yes, this helped
              </button>
              <button
                onClick={() => console.log('Not helpful feedback')}
                className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors flex items-center gap-2"
              >
                <XCircle className="w-5 h-5" />
                No, I need more help
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Links Footer */}
      <div className="bg-white border-t">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
            <a href="/legal/privacy" className="hover:text-sky-600 flex items-center gap-1">
              <Lock className="w-4 h-4" />
              Privacy Policy
            </a>
            <span className="text-gray-400">•</span>
            <a href="/legal/terms" className="hover:text-sky-600 flex items-center gap-1">
              <FileText className="w-4 h-4" />
              Terms of Service
            </a>
            <span className="text-gray-400">•</span>
            <a href="/contact" className="hover:text-sky-600 flex items-center gap-1">
              <MessageCircle className="w-4 h-4" />
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;